import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import useSWR from 'swr';
import {
  Layout,
  LoadingSpinner,
  ErrorMessage,
  RichTextEditor
} from '@/shared/components';
import { useFormSubmission } from '@/shared/hooks/useFormSubmission';
import { customerInformationApi } from '@/shared/api';
import { API_ENDPOINTS } from '@/shared/config/api';
import {
  customerInformationSchema,
  type CustomerInformationFormData
} from '@/shared/schemas';

export const CustomerInformationPage: React.FC = () => {
  const navigate = useNavigate();

  const {
    data: response,
    error: loadError,
    isLoading,
    mutate
  } = useSWR(
    API_ENDPOINTS.CUSTOMER_INFORMATION.GET,
    () => customerInformationApi.get(),
    { revalidateOnFocus: false }
  );

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    reset
  } = useForm<CustomerInformationFormData>({
    resolver: zodResolver(customerInformationSchema),
    defaultValues: { contentHtml: '' }
  });

  useEffect(() => {
    if (response?.success && response.content) {
      reset({ contentHtml: response.content.contentHtml });
    }
  }, [response, reset]);

  const { isSubmitting, handleSubmit: handleFormSubmit } =
    useFormSubmission<CustomerInformationFormData>({
      onSubmit: async (data) => {
        const result = await customerInformationApi.update({
          contentHtml: data.contentHtml
        });
        if (result.success) {
          await mutate();
          reset({ contentHtml: result.content.contentHtml });
        }
      },
      successMessage: 'Содержимое успешно сохранено',
      errorMessage: 'Ошибка при сохранении содержимого'
    });

  const handleBack = (): void => {
    navigate('/pages');
  };

  if (isLoading) {
    return (
      <Layout title="Загрузка...">
        <div className="card card-bordered bg-base-100 shadow-sm">
          <div className="card-body p-2">
            <div className="flex justify-center py-8">
              <LoadingSpinner size="lg" text="Загрузка содержимого..." />
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (loadError || !response?.success) {
    return (
      <Layout title="Ошибка">
        <div className="card card-bordered bg-base-100 shadow-sm">
          <div className="card-body p-2">
            <ErrorMessage message="Ошибка загрузки содержимого. Попробуйте обновить страницу." />
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Информация для потребителей">
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <p className="text-base-content/60 mt-1">
            Редактирование содержимого страницы /customer-information
          </p>
          <button
            onClick={handleBack}
            className="btn btn-secondary"
            disabled={isSubmitting}
          >
            <i className="fas fa-arrow-left"></i>
            Назад к списку
          </button>
        </div>

        <div className="card card-bordered bg-base-100 shadow-sm">
          <div className="card-body p-6">
            <form
              onSubmit={handleSubmit(handleFormSubmit)}
              className="space-y-6"
            >
              <div className="form-control">
                <label className="label">
                  <span className="label-text font-medium flex items-center gap-2">
                    <i className="fas fa-file-alt text-primary"></i>
                    Содержимое страницы
                  </span>
                </label>
                <Controller
                  name="contentHtml"
                  control={control}
                  render={({ field }) => (
                    <RichTextEditor
                      value={field.value}
                      onChange={field.onChange}
                      disabled={isSubmitting}
                      onUploadImage={customerInformationApi.uploadImage}
                      onUploadDocument={customerInformationApi.uploadDocument}
                    />
                  )}
                />
                {errors.contentHtml && (
                  <label className="label">
                    <span className="label-text-alt text-error">
                      {errors.contentHtml.message}
                    </span>
                  </label>
                )}
                <label className="label">
                  <span className="label-text-alt text-base-content/60">
                    Отображается как HTML на публичной странице
                    /customer-information
                  </span>
                </label>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 pt-6">
                <button
                  type="submit"
                  className="btn btn-primary flex-1 sm:flex-none"
                  disabled={isSubmitting || !isDirty}
                >
                  {isSubmitting ? (
                    <>
                      <span className="loading loading-spinner loading-sm"></span>
                      Сохранение...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-save"></i>
                      Сохранить
                    </>
                  )}
                </button>

                <button
                  type="button"
                  onClick={handleBack}
                  className="btn btn-secondary flex-1 sm:flex-none"
                  disabled={isSubmitting}
                >
                  <i className="fas fa-times"></i>
                  Отмена
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </Layout>
  );
};
